/**
 * Copies Constructor's client id (`i`) and session id (`s`) into cart attributes
 * so they survive the trip to checkout (ECOMDEV-967).
 *
 * Checkout surfaces can't reach the storefront tracker: checkout UI extensions
 * can't read this domain's cookies or localStorage at all, and the order webhook
 * only sees the order. Cart attributes reach all of them — as
 * `checkout.attributes` in the Constructor checkout pixel
 * (docs/constructor-checkout-pixel.js) and the checkout upsell beacons, and as
 * `note_attributes` in the app's orders/create purchase — under these keys.
 *
 * Reads the ids the tracker persisted — the native vendor SDK or the optimized
 * cnstrc-dom-tracker.js, whose storage layouts differ — once it has initialised,
 * and writes them only when they differ from what the cart already holds. Runs
 * on load for a non-empty cart and on every successful cart update, so the first
 * add-to-cart stamps a new cart before the shopper can reach checkout.
 */
(() => {
  if (window.__swCioCartIdentityInstalled) return;
  window.__swCioCartIdentityInstalled = true;

  const CLIENT_ID_ATTRIBUTE = '_constructorio_client_id';
  const SESSION_ID_ATTRIBUTE = '_constructorio_session_id';
  const TRACKER_POLL_MS = 250;
  const TRACKER_WAIT_MS = 10000;

  const getCookie = (name) => {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  };

  const getLocalStorageItem = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  };

  // `{sessionId, lastTime}` from the native SDK and the optimized tracker's
  // cookie, `{id, ts}` from the optimized tracker's localStorage.
  const parseSessionRecord = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const id = Number(parsed.sessionId ?? parsed.id);
      const writtenAt = Number(parsed.lastTime ?? parsed.ts) || 0;
      return Number.isInteger(id) && id > 0 ? { id, writtenAt } : null;
    } catch (_) {
      return null;
    }
  };

  const parseSessionId = (raw) => {
    const id = Number(raw);
    return raw && Number.isInteger(id) && id > 0 ? id : null;
  };

  const readClientId = () =>
    getCookie('ConstructorioID_client_id') ||
    getLocalStorageItem('_constructorio_search_client_id');

  const readSessionId = () => {
    // Either copy can be stale — a localStorage write that failed, or a cookie
    // left behind by the other tracker mode — so take the one written last.
    const [latest] = [
      parseSessionRecord(getLocalStorageItem('_constructorio_search_session')),
      parseSessionRecord(getCookie('ConstructorioID_session')),
    ]
      .filter(Boolean)
      .sort((a, b) => b.writtenAt - a.writtenAt);
    const id =
      latest?.id ??
      parseSessionId(getLocalStorageItem('_constructorio_search_session_id')) ??
      parseSessionId(getCookie('ConstructorioID_session_id'));
    return id ? String(id) : null;
  };

  // The native SDK exposes window.ConstructorioTracker and cnstrc-dom-tracker.js
  // window.ConstructorTracker once they have written this visit's ids. Reading
  // earlier finds nothing on a first visit, or a session about to be rolled over.
  const trackerReady = new Promise((resolve) => {
    const isReady = () => Boolean(window.ConstructorioTracker || window.ConstructorTracker);
    if (isReady()) return resolve();
    let waited = 0;
    const timer = setInterval(() => {
      waited += TRACKER_POLL_MS;
      if (isReady() || waited >= TRACKER_WAIT_MS) {
        clearInterval(timer);
        resolve();
      }
    }, TRACKER_POLL_MS);
  });

  const identityKey = (clientId, sessionId) => `${clientId || ''}|${sessionId || ''}`;

  let syncedKey = identityKey(Theme.cart_constructor_client_id, Theme.cart_constructor_session_id);

  async function syncCartIdentity() {
    const clientId = readClientId();
    const sessionId = readSessionId();
    if (!clientId && !sessionId) return;

    const key = identityKey(clientId, sessionId);
    if (key === syncedKey) return;

    const attributes = {};
    if (clientId) attributes[CLIENT_ID_ATTRIBUTE] = clientId;
    if (sessionId) attributes[SESSION_ID_ATTRIBUTE] = sessionId;

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        // Some flows navigate to /checkout right after adding to the cart.
        keepalive: true,
      });
      // On failure the next cart update tries again.
      if (response.ok) syncedKey = key;
    } catch (_) {}
  }

  // One write at a time, so an older write can never land after a newer one.
  let queue = trackerReady;
  const scheduleSync = () => {
    queue = queue.then(syncCartIdentity).catch(() => {});
  };

  if (Number(Theme.cart_item_count) > 0) scheduleSync();
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.didError) return;
    scheduleSync();
  });
})();
